import ReactDOM from 'react-dom'
import React, {RefObject} from 'react'
import * as css from './CustomScrollbar.css'
import {Component} from '$src/handy'
import {createRef} from 'react'
import DraggableArea from './DraggableArea'

interface Props {
  refToScrollableEl: RefObject<HTMLElement>
  aboveSummary: boolean
}

interface State {
  ready: boolean
  scrollableElState: {
    scrollWidth: number
    scrollLeft: number
    width: number
  }
}

/**
 * We're going with a custom scrollbar implementation because of its simplicity.
 * I tried using a css trick (https://stackoverflow.com/questions/18997724/how-to-change-scroll-bar-position-with-css),
 * but it's a hack and turned out to be buggy.
 *
 * I also tried using an existing custom scrollbar library, but nothing
 * fit exactly the needs of this scrollbar.
 *
 * We need the scrollbar to have a fixed position (so it the map or body's scroll doesn't
 * occlude it), and other than that, we need it to be simple and efficient.
 *
 * This implementation gives us that. I'd have preferred to write something more generic
 * in a separate library, but since client won't benefit from that, I decided to
 * write one specific to our needs.
 */
export default class CustomScrollbar extends Component<Props, State> {
  scrollbarRef = createRef<HTMLDivElement>()
  lastAnimFrameReq: number | null
  interval: ReturnType<typeof setInterval>
  state = {
    ready: false,
    scrollableElState: {
      scrollWidth: 0,
      scrollLeft: 0,
      width: 0,
    },
  }
  stateBeforeDrag: State = this.state

  render() {
    const stuff = this.getStuff(this.state)

    const scrollerScaleX = stuff.scrollerWidth / 1000
    return ReactDOM.createPortal(
      <div className={this.props.aboveSummary ? css.scrollbarRaised : css.scrollbar} ref={this.scrollbarRef}>
        <DraggableArea
          onDragStart={this.dragStart}
          onDrag={this.drag}
          onDragEnd={this.dragEnd}
          shouldReturnMovement={true}
          lockCursorTo="ew-resize"
        >
          <div
            className={css.scroller}
            style={{
              transform: `translateX(${stuff.scrollerLeft}px) scale(${scrollerScaleX}, 1)`,
            }}
          ></div>
        </DraggableArea>
      </div>,
      document.body,
    )
  }

  dragStart = () => {
    this.stateBeforeDrag = this.state
  }

  drag = (dx: number) => {
    const stuff = this.getStuff(this.stateBeforeDrag)
    this.props.refToScrollableEl.current!.scrollLeft +=
      (dx * this.stateBeforeDrag.scrollableElState.scrollWidth) /
      stuff.scrollbarWidth
  }

  dragEnd = () => {}

  async componentDidMount() {
    // ensures refs are ready
    await new Promise(resolve => {
      const interval = setInterval(() => {
        if (this.props.refToScrollableEl.current && this.scrollbarRef.current) {
          clearInterval(interval)
          resolve()
        }
      }, 100)
    })
    this.readDims()
    this.interval = setInterval(() => {
      this.lastAnimFrameReq = requestAnimationFrame(() => {
        this.lastAnimFrameReq = null
        this.readDims()
      })
    }, 500)
    this.props.refToScrollableEl.current!.addEventListener(
      'scroll',
      this.readDims,
      {passive: false},
    )
    this.setState({ready: true})
  }

  getStuff(
    state: State,
  ): {scrollerWidth: number; scrollerLeft: number; scrollbarWidth: number} {
    if (!state.ready)
      return {
        scrollerWidth: 0,
        scrollerLeft: 0,
        scrollbarWidth: 1,
      }
    const scrollbarWidth = this.scrollbarRef.current!.getBoundingClientRect()
      .width

    const scrollableElState = state.scrollableElState
    const scrollerLeft =
      (scrollableElState.scrollLeft / scrollableElState.scrollWidth) *
      scrollbarWidth
    const scrollerWidth =
      (scrollableElState.width / scrollableElState.scrollWidth) * scrollbarWidth
    return {
      scrollerWidth,
      scrollerLeft,
      scrollbarWidth,
    }
  }

  readDims = () => {
    const scrollableEl = this.props.refToScrollableEl.current!
    this.setState({
      scrollableElState: {
        scrollLeft: scrollableEl.scrollLeft,
        scrollWidth: scrollableEl.scrollWidth,
        width: scrollableEl.getBoundingClientRect().width,
      },
    })
  }

  componentWillUnmount() {
    clearInterval(this.interval)
    if (this.lastAnimFrameReq) cancelAnimationFrame(this.lastAnimFrameReq)
  }
}



// WEBPACK FOOTER //
// ./src/common/CustomScrollbar.tsx